import React from 'react';
import {
  Button,
  Chip,
  MenuItem,
  Rating,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import moment from 'moment';
import {
  Link,
  useHistory,
  useParams,
} from 'react-router-dom';

import {
  AccountTable,
  DashboardError,
  DashboardPage,
  IAccountTableColumn,
  HiringTeamPanel,
} from '../../components/JobsAccount';
import { NoAccessMessage } from '../../components/NoAccessMessage';
import { book } from '../../routers/books';
import {
  useGetJobApplicantsQuery,
  useUpdateApplicationStatusMutation,
} from '../../services/serviceJobsAccount';
import { IJobApplication } from '../../services/serviceJobsAccount/interfaces';
import { downloadCsv } from '../../utils/csv';
import { isForbidden } from '../../utils/jobAccessError';

const formatDate = (date: string | null): string => (
  date ? moment(date).format('MM/DD/YYYY') : '—'
);

const STAGE_NAMES: Record<number, string> = {
  1: 'New',
  2: 'Reviewing',
  3: 'Interview',
  4: 'Offer',
  5: 'Hired',
  6: 'Rejected',
};
const stageName = (statusId: number): string => STAGE_NAMES[statusId] || '';

const headerActionStyles = {
  fontSize: '0.75rem',
  fontWeight: 700,
  minHeight: 48,
  px: 2.5,
  whiteSpace: 'nowrap',
} as const;

export const JobsApplicants = () => {
  const { guid } = useParams<{ guid: string }>();
  const history = useHistory();
  const { data, error, isLoading, isFetching } = useGetJobApplicantsQuery(guid, {
    skip: !guid,
  });
  const applications = data?.jobApplications || [];
  const [updateStatus, updateStatusResult] = useUpdateApplicationStatusMutation();

  const columns: IAccountTableColumn<IJobApplication>[] = [
    {
      id: 'from',
      label: 'From',
      render: (application) => (
        <Button
          variant="text"
          onClick={() => history.push(
            book.jobsApplicantDetail(
              data?.eguid || guid,
              application.applicationId
            )
          )}
          sx={{
            fontWeight: application.isRead ? 400 : 800,
            textAlign: 'left',
          }}
        >
          {application.name} ({application.from})
        </Button>
      ),
    },
    {
      id: 'status',
      label: 'Status',
      render: (application) => (
        <Chip
          size="small"
          color={application.isRead ? 'default' : 'primary'}
          label={application.isRead ? 'Viewed' : 'New'}
          sx={{ borderRadius: 0, fontWeight: 700 }}
        />
      ),
    },
    {
      id: 'dateSent',
      label: 'Date Sent',
      render: (application) => formatDate(application.dateSent),
    },
    {
      id: 'stage',
      label: 'Stage',
      render: (application) => (
        <TextField
          select
          size="small"
          value={application.statusId}
          disabled={updateStatusResult.isLoading}
          onChange={(event) => updateStatus({
            eguid: data?.eguid || guid,
            applicationId: application.applicationId,
            statusId: Number(event.target.value),
          })}
          sx={{ minWidth: 140 }}
        >
          {[[1, 'New'], [2, 'Reviewing'], [3, 'Interview'], [4, 'Offer'], [5, 'Hired'], [6, 'Rejected']]
            .map(([id, name]) => <MenuItem key={id} value={id}>{name}</MenuItem>)}
        </TextField>
      ),
    },
    {
      id: 'rating',
      label: 'Rating',
      render: (application) => <Rating value={application.rating || 0} size="small" readOnly />,
    },
    {
      id: 'appliedVia',
      label: 'Applied Via',
      render: (application) => application.appliedVia || '—',
    },
  ];

  const exportApplications = () => {
    downloadCsv(`job-applicants-${data?.adTitle || 'listing'}`, [
      ['Name', 'Email', 'Date Sent', 'Applied Via', 'Read', 'Stage', 'Rating', 'Notes', 'Message'],
      ...applications.map((application) => [
        application.name,
        application.from,
        formatDate(application.dateSent),
        application.appliedVia || '',
        application.isRead ? 'Yes' : 'No',
        application.statusName || stageName(application.statusId),
        application.rating || '',
        application.noteCount,
        application.message || '',
      ]),
    ]);
  };

  return (
    <DashboardPage
      title={data ? `Applications for ${data.adTitle}` : 'Manage Applications'}
      noAccessComponent={isForbidden(error) ? <NoAccessMessage /> : undefined}
      actions={(
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Button
            component={Link}
            to={book.jobsListings}
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            sx={headerActionStyles}
          >
            Back to Listings
          </Button>
          <HiringTeamPanel eguid={data?.eguid || guid} />
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={exportApplications}
            disabled={applications.length === 0}
            sx={headerActionStyles}
          >
            Export CSV
          </Button>
        </Stack>
      )}
    >
      <DashboardError error={error} fallback="We could not load the applications for this listing." />
      <DashboardError
        error={updateStatusResult.error}
        fallback="We could not update that applicant’s stage. Please try again."
      />
      {!error && data?.company ? (
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          {data.company}
        </Typography>
      ) : null}
      <AccountTable
        ariaLabel={`Applications for ${data?.adTitle || 'this job listing'}`}
        columns={columns}
        rows={applications}
        getRowId={(application) => application.applicationId}
        emptyMessage="There are no applications for this job listing yet."
        isLoading={isLoading || isFetching}
      />
    </DashboardPage>
  );
};

export default JobsApplicants;
